/**
 * GQty: You can safely modify this file based on your needs.
 */

import { createReactClient } from "@gqty/react";
import { Cache, createClient, type QueryFetcher } from "gqty";
import {
  generatedSchema,
  scalarsEnumsHash,
  type GeneratedSchema,
} from "./schema.generated";
import {
  isRequestInstrumentationEnabled,
  requestInstrumentationHeader,
} from "app/devTools/requestInstrumentation";
import { getApiUrl } from "lib/apiUrl";
import {
  actAsFetch,
  assertActAsRequestCurrent,
  captureActAsRequest,
  registerActAsCacheClearer,
} from "app/actAsSession";
import { responseHandler } from "./responseHandler";
import { withQueryRequestDeadline } from "./requestDeadline";

const requestScopes = new WeakMap<
  object,
  ReturnType<typeof captureActAsRequest>
>();

const queryFetcher: QueryFetcher = async function (payload, fetchOptions) {
  const { query, variables, operationName } = payload;
  // GQty reuses its payload when retrying. Keep that operation bound to the
  // identity of its first attempt, including when a base-user request retries.
  const scope = requestScopes.get(payload) ?? captureActAsRequest();
  requestScopes.set(payload, scope);
  assertActAsRequestCurrent(scope);

  return withQueryRequestDeadline(
    payload,
    async (signal) => {
      const response = await actAsFetch(getApiUrl("/api/graphql"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(isRequestInstrumentationEnabled()
            ? { [requestInstrumentationHeader]: "1" }
            : {}),
        },
        body: JSON.stringify({
          query,
          variables,
          operationName,
        }),
        mode: "cors",
        ...fetchOptions,
        signal,
      });

      const result = await responseHandler(response);
      assertActAsRequestCurrent(scope);
      return result;
    },
    fetchOptions?.signal,
  );
};

const cache = new Cache(undefined, {
  maxAge: 5 * 1000,
  staleWhileRevalidate: 0,
  normalization: false,
});

export const client = createClient<GeneratedSchema>({
  schema: generatedSchema,
  scalars: scalarsEnumsHash,
  cache,
  fetchOptions: {
    fetcher: queryFetcher,
  },
});

registerActAsCacheClearer(() => client.cache.clear());

// Core functions
export const { resolve, subscribe, schema } = client;

// Legacy functions
export const {
  query,
  mutation,
  mutate,
  subscription,
  resolved,
  refetch,
  track,
} = client;

export const {
  graphql,
  useQuery,
  usePaginatedQuery,
  useTransactionQuery,
  useLazyQuery,
  useRefetch,
  useMutation,
  useMetaState,
  prepareReactRender,
  useHydrateCache,
  prepareQuery,
} = createReactClient<GeneratedSchema>(client, {
  defaults: {
    // Enable Suspense, you can override this option for each hook.
    //suspense: true,
  },
});

export * from "./schema.generated";
